import { CloseOutlined, MoreOutlined, PrinterOutlined } from '@ant-design/icons';
import { DeleteButtonProps, EditButtonProps, RefreshButton } from '@refinedev/antd';
import { Button, ButtonProps, Flex, Tooltip } from 'antd';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import MessageChat from '@/components/general/MessageChat/MessageChat';
import PopupMenu from '@/components/general/PopupMenu';
import ProtectedDeleteButton from '@/components/protected/ProtectedDeleteButton';
import ProtectedEditButton from '@/components/protected/ProtectedEditButton';
import { useUiContext } from '@/contexts/UiContext';
import { ENTITY_TYPE } from '@/model/General/MessageChat';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { Shipment } from '@/model/Logistic/Shipment/Shipment';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';
import { RESOURCES } from '@/Resources';

import useShipmentHeaderActions from './useShipmentHeaderActions';

interface ShipmentShowHeaderButtonsProps {
  record: Shipment;
  refineButtonsProps: {
    editButtonProps: EditButtonProps | undefined;
    deleteButtonProps: DeleteButtonProps | undefined;
  };
  refetch: () => void;
  blockChangeState: { id: string; message: string }[];
  canSend?: boolean;
  isPaid?: boolean;
  showQuantityAlert?: boolean;
  /** Metadata: indica si el archivo tributario fue cargado, para bloquear avance de estado */
  hasTaxFileUploaded?: boolean;
  /** Bloquea acciones de cambio de estado por mismatch de país */
  blockForCountry?: boolean;
}

const ShipmentShowHeaderButtons = (props: ShipmentShowHeaderButtonsProps) => {
  const { isMediumScreen } = useUiContext();
  const { packageRepository } = useRepositoriesContext();
  const { t } = useTranslation();
  const {
    record,
    refineButtonsProps,
    refetch,
    blockChangeState,
    canSend,
    isPaid,
    showQuantityAlert,
    hasTaxFileUploaded,
    blockForCountry,
  } = props;
  const { editButtonProps, deleteButtonProps } = refineButtonsProps;
  const { id, status } = record;
  const {
    changeStateButtonsProps,
    changeStateBlockTooltip,
    disableStateButtons,
    showCancelButton,
    onCancelClick,
    contextHolder,
    notificationContextHolder,
  } = useShipmentHeaderActions({
    record,
    refetch,
    blockChangeState,
    canSend,
    isPaid,
    showQuantityAlert,
    hasTaxFileUploaded,
  });
  // --- Botones de cambio de estado renderizados ---
  const countryBlockTooltip = blockForCountry
    ? t('Selecciona el país correcto para modificar este recurso')
    : '';
  const changeStateButtons = useMemo(
    () =>
      changeStateButtonsProps.length > 0
        ? changeStateButtonsProps.map((btnProps: ButtonProps, index) => (
            <Tooltip key={index} title={countryBlockTooltip || changeStateBlockTooltip || ''}>
              <Button
                key={'change-state-' + index}
                type={'primary'}
                color={isMediumScreen ? 'primary' : undefined}
                {...btnProps}
                disabled={blockForCountry || disableStateButtons || btnProps.disabled}
              />
            </Tooltip>
          ))
        : null,
    [
      changeStateBlockTooltip,
      changeStateButtonsProps,
      disableStateButtons,
      isMediumScreen,
      blockForCountry,
      countryBlockTooltip,
    ],
  );
  // --- Botón de cancelar ---
  const cancelButton = useMemo(
    () =>
      showCancelButton ? (
        <Tooltip title={countryBlockTooltip || changeStateBlockTooltip}>
          <Button
            key={'cancel'}
            disabled={blockForCountry || disableStateButtons}
            danger
            icon={<CloseOutlined />}
            onClick={onCancelClick}
            type={isMediumScreen ? 'text' : 'default'}>
            {t('Cancelar')}
          </Button>
        </Tooltip>
      ) : null,
    [
      showCancelButton,
      changeStateBlockTooltip,
      disableStateButtons,
      onCancelClick,
      isMediumScreen,
      t,
      blockForCountry,
      countryBlockTooltip,
    ],
  );
  // --- Contenido del menú popup ---
  const popupMenuContent: ReactNode[] = useMemo(() => {
    const buttons: ReactNode[] = [];

    if (isMediumScreen && changeStateButtons) {
      buttons.push(changeStateButtons);
    }

    if (
      status === PACKAGE_STATES.PENDIENTE ||
      status === PACKAGE_STATES.PREPARACION ||
      status === PACKAGE_STATES.PREPARADO
    ) {
      buttons.push(
        <Button
          key={'print'}
          icon={<PrinterOutlined />}
          type="text"
          onClick={() => id && void packageRepository.printShippingLabel(id, RESOURCES.SHIPMENTS)}>
          {t('Imprimir etiqueta')}
        </Button>,
      );
    }

    if (status === PACKAGE_STATES.PENDIENTE) {
      buttons.push(<ProtectedEditButton key={'edit'} {...editButtonProps} type="text" />);
    }

    if (isMediumScreen && cancelButton) {
      buttons.push(cancelButton);
    }

    if (status === PACKAGE_STATES.PENDIENTE) {
      buttons.push(
        <ProtectedDeleteButton
          key="delete"
          {...deleteButtonProps}
          type="text"
          disabled={blockForCountry}
        />,
      );
    }

    return buttons;
  }, [
    cancelButton,
    changeStateButtons,
    deleteButtonProps,
    editButtonProps,
    id,
    isMediumScreen,
    packageRepository,
    status,
    t,
    blockForCountry,
  ]);

  return (
    <Flex gap={8} wrap align="center" style={{ minHeight: '40px' }}>
      <RefreshButton
        key="refresh"
        onClick={() => void refetch()}
        hideText
        size={isMediumScreen ? 'small' : 'middle'}
      />
      {!isMediumScreen && (
        <>
          {cancelButton} {changeStateButtons}
        </>
      )}

      {popupMenuContent.length > 0 && (
        <PopupMenu
          openButton={<Button icon={<MoreOutlined />} size={isMediumScreen ? 'small' : 'middle'} />}
          disabled={disableStateButtons}
          tooltip={changeStateBlockTooltip}
          menuContent={
            <Flex vertical align="start" gap={8}>
              {popupMenuContent}
            </Flex>
          }
        />
      )}
      {contextHolder}
      {notificationContextHolder}
      <MessageChat entityType={ENTITY_TYPE.ENVIO} entityId={id} title={t('Mensajes del envío')} />
    </Flex>
  );
};

export default ShipmentShowHeaderButtons;
